import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { checkEligibility } from "../../../Services/corporate";
import { corporateSignupOtp } from "../../../Services/auth";
import amplitudeService from "../../../Services/amplitude";

// ─── Validation schema ─────────────────────────────────────────────────────
const schema = z
  .object({
    first_name: z
      .string()
      .trim()
      .min(1, "First name is required")
      .max(100, "Too long"),
    last_name: z
      .string()
      .trim()
      .min(1, "Last name is required")
      .max(100, "Too long"),
    department: z.string().trim().max(200).optional().or(z.literal("")),
    role_title: z.string().trim().max(200).optional().or(z.literal("")),
    work_email: z
      .string()
      .trim()
      .min(1, "Work email is required")
      .email("Enter a valid email"),
    personal_email: z
      .string()
      .trim()
      .min(1, "Personal email is required")
      .email("Enter a valid email"),
    confirm_personal_email: z
      .string()
      .trim()
      .min(1, "Please confirm your personal email"),
  })
  .refine((data) => data.personal_email === data.confirm_personal_email, {
    message: "Personal emails do not match",
    path: ["confirm_personal_email"],
  });

// ─── User-visible error mapping (per spec §5.2) ────────────────────────────
const ELIGIBILITY_REASON_MESSAGE = {
  corporate_not_found: "This activation page is no longer available.",
  email_not_eligible:
    "We can't verify you as a {orgName} team member with this work email. Try a different email or contact your admin.",
  already_enrolled_elsewhere:
    "This personal email is already enrolled with another enterprise program. Please use a different personal email.",
  member_already_signed_up:
    "An account already exists for this work email. Please log in instead.",
  seat_limit_reached:
    "Your organization has reached its activation limit. Please contact your admin.",
};

const formatReason = (reason, orgName) =>
  (ELIGIBILITY_REASON_MESSAGE[reason] || "Something went wrong. Please try again.").replace(
    "{orgName}",
    orgName || "your organization"
  );

// ─── Reusable field row ────────────────────────────────────────────────────
// `flex-1 min-w-0` lets two side-by-side Fields split available width evenly
// on desktop (when their flex-row parent is active) and stay full-width on
// mobile (when the parent is flex-col).
const Field = ({ label, error, children, htmlFor }) => (
  <div className="flex flex-col gap-2 flex-1 min-w-0">
    <label
      htmlFor={htmlFor}
      className="text-base font-semibold text-black"
    >
      {label}
    </label>
    {children}
    {error && (
      <span className="text-sm text-[#d92d20] mt-0.5" role="alert">
        {error}
      </span>
    )}
  </div>
);

const inputClass =
  "w-full h-12 px-4 rounded-xl border border-[#d0d5dd] bg-white text-base text-black placeholder:text-[#7e7f83] shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] focus:outline-none focus:border-[#0d5eaf] focus:shadow-[0px_0px_0px_4px_rgba(13,94,175,0.12)] transition-shadow";

/**
 * Activation form (Verify step). Submits eligibility check, then on success
 * triggers OTP send and navigates to /enterprise/:slug/setup.
 *
 * @param {Object} props
 * @param {string} props.slug
 * @param {string} props.orgName - corporate display name (for error messages)
 * @param {Array<{name:string}>} props.departments
 * @param {Array<{name:string}>} props.roles
 * @param {string} props.buttonLabel
 */
const ActivationForm = ({
  slug,
  orgName,
  departments = [],
  roles = [],
  buttonLabel = "Activate Access",
}) => {
  const navigate = useNavigate();
  const [topLevelError, setTopLevelError] = useState(null);
  const [memberAlreadyExists, setMemberAlreadyExists] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: "",
      last_name: "",
      department: "",
      role_title: "",
      work_email: "",
      personal_email: "",
      confirm_personal_email: "",
    },
  });

  const showDepartments = departments.length > 0;
  const showRoles = roles.length > 0;

  const onSubmit = async (values) => {
    setTopLevelError(null);
    setMemberAlreadyExists(false);

    const payload = {
      slug,
      work_email: values.work_email,
      personal_email: values.personal_email,
      first_name: values.first_name,
      last_name: values.last_name,
      department: values.department || undefined,
      role_title: values.role_title || undefined,
    };

    let eligibility;
    try {
      eligibility = await checkEligibility(payload);
    } catch (err) {
      setTopLevelError(
        "We couldn't reach the activation service. Please try again in a moment."
      );
      return;
    }

    amplitudeService.track?.("corporate_eligibility_checked", {
      slug,
      eligible: !!eligibility?.eligible,
      reason: eligibility?.reason,
    });

    if (!eligibility?.eligible) {
      const message = formatReason(eligibility?.reason, orgName);
      // Map specific reasons to specific fields for inline UX
      if (eligibility?.reason === "email_not_eligible") {
        setError("work_email", { message });
      } else if (eligibility?.reason === "already_enrolled_elsewhere") {
        setError("personal_email", { message });
      } else if (eligibility?.reason === "member_already_signed_up") {
        setMemberAlreadyExists(true);
        setTopLevelError(message);
      } else {
        setTopLevelError(message);
      }
      return;
    }

    // Send the work-email OTP. Backend still records personal_email on the
    // OTP session even though we no longer verify it client-side.
    try {
      const otpResult = await corporateSignupOtp({
        member_id: eligibility.member_id,
        work_email: values.work_email,
        personal_email: values.personal_email,
      });
      amplitudeService.track?.("corporate_signup_otp_sent", { slug });

      // Stash short-lived session for the Setup step. Use sessionStorage —
      // NOT localStorage (per spec).
      sessionStorage.setItem(
        "corporate_signup_session",
        JSON.stringify({
          otp_session_id: otpResult.otp_session_id,
          member_id: eligibility.member_id,
          corporate_id: eligibility.corporate_id,
          slug,
          work_email: values.work_email,
          personal_email: values.personal_email,
          first_name: values.first_name,
        })
      );

      navigate(`/enterprise/${encodeURIComponent(slug)}/setup`, {
        state: {
          otp_session_id: otpResult.otp_session_id,
          member_id: eligibility.member_id,
          corporate_id: eligibility.corporate_id,
          work_email: values.work_email,
          personal_email: values.personal_email,
          first_name: values.first_name,
        },
      });
    } catch (err) {
      const status = err?.status || err?.status_code;
      if (status === 429) {
        setTopLevelError(
          "You've requested too many codes — please wait an hour and try again."
        );
      } else {
        setTopLevelError(
          "We couldn't send your verification code. Please try again in a moment."
        );
      }
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="flex flex-col gap-7"
    >
      {/* First / Last name (side-by-side on desktop) */}
      <div className="flex flex-col md:flex-row gap-4">
        <Field
          label="First Name"
          htmlFor="first_name"
          error={errors.first_name?.message}
        >
          <input
            id="first_name"
            type="text"
            autoComplete="given-name"
            className={inputClass}
            {...register("first_name")}
          />
        </Field>
        <Field
          label="Last Name"
          htmlFor="last_name"
          error={errors.last_name?.message}
        >
          <input
            id="last_name"
            type="text"
            autoComplete="family-name"
            className={inputClass}
            {...register("last_name")}
          />
        </Field>
      </div>

      {/* Department — hidden when lookup is empty per the user's directive */}
      {showDepartments && (
        <Field
          label="Department"
          htmlFor="department"
          error={errors.department?.message}
        >
          <div className="relative">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-black pointer-events-none"
            />
            <select
              id="department"
              className={`${inputClass} pl-11 appearance-none w-full`}
              {...register("department")}
            >
              <option value="">Select…</option>
              {departments.map((dept) => (
                <option key={dept.name} value={dept.name}>
                  {dept.name}
                </option>
              ))}
            </select>
          </div>
        </Field>
      )}

      {/* Role — hidden when lookup is empty */}
      {showRoles && (
        <Field
          label="Role"
          htmlFor="role_title"
          error={errors.role_title?.message}
        >
          <select
            id="role_title"
            className={`${inputClass} appearance-none w-full`}
            {...register("role_title")}
          >
            <option value="">Select…</option>
            {roles.map((role) => (
              <option key={role.name} value={role.name}>
                {role.name}
              </option>
            ))}
          </select>
        </Field>
      )}

      {/* Work email */}
      <Field
        label="Work Email"
        htmlFor="work_email"
        error={errors.work_email?.message}
      >
        <input
          id="work_email"
          type="email"
          autoComplete="email"
          className={inputClass}
          {...register("work_email")}
        />
      </Field>

      {/* Personal / confirm email */}
      <div className="flex flex-col md:flex-row gap-4">
        <Field
          label="Personal Email"
          htmlFor="personal_email"
          error={errors.personal_email?.message}
        >
          <input
            id="personal_email"
            type="email"
            className={inputClass}
            {...register("personal_email")}
          />
        </Field>
        <Field
          label="Confirm Personal Email"
          htmlFor="confirm_personal_email"
          error={errors.confirm_personal_email?.message}
        >
          <input
            id="confirm_personal_email"
            type="email"
            className={inputClass}
            {...register("confirm_personal_email")}
            onPaste={(e) => e.preventDefault()}
          />
        </Field>
      </div>

      {topLevelError && (
        <div
          role="alert"
          className="rounded-md border border-[#fda29b] bg-[#fef3f2] px-3 py-2 text-sm text-[#b42318]"
        >
          <p className="m-0">{topLevelError}</p>
          {memberAlreadyExists && (
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="mt-2 inline-flex items-center text-sm font-medium text-[#0d5eaf] underline"
            >
              Go to login
            </button>
          )}
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="corp-primary-button"
      >
        {isSubmitting ? "Verifying…" : buttonLabel}
      </button>
    </form>
  );
};

export default ActivationForm;
